import { _DJB2, _makeTypedGet } from '@statsig/client-core';
const LOCAL_OVERRIDE_REASON = 'LocalOverride:Recognized';
export const LOCAL_STORAGE_KEY = 'STATSIG_OVERRIDES';
const LEGACY_LOCAL_STORAGE_KEY = 'STATSIG_JS_LITE_LOCAL_OVERRIDES';
const makeEmptyStore = () => ({
  gates: {},
  configs: {},
  layers: {}
});

/**
 * Custom implementation of `@statsig/js-local-overrides` package with support for local storage
 * so we can keep the existing behavior where overrides are cached locally. Also designed for
 * compatibility with the old override system (eg. no `experiments` field, `configs` is used
 * instead).
 *
 * [Reference](https://github.com/statsig-io/js-client-monorepo/blob/main/packages/js-local-overrides/src/LocalOverrideAdapter.ts)
 */
export class PersistentOverrideAdapter {
  constructor() {
    this._overrides = makeEmptyStore();
  }
  parseStoredOverrides(localStorageKey) {
    try {
      const json = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!json) {
        return makeEmptyStore();
      }
      return JSON.parse(json);
    } catch {
      return makeEmptyStore();
    }
  }
  mergeOverrides(...allOverrides) {
    const merged = makeEmptyStore();
    for (const overrides of allOverrides) {
      for (const [name, value] of Object.entries(overrides.gates)) {
        merged.gates[name] = value;
      }
      for (const [name, value] of Object.entries(overrides.configs)) {
        merged.configs[name] = value;
      }
      for (const [name, value] of Object.entries(overrides.layers)) {
        merged.layers[name] = value;
      }
    }
    return merged;
  }
  initFromStoredOverrides() {
    this._overrides = this.mergeOverrides(this.parseStoredOverrides(LEGACY_LOCAL_STORAGE_KEY), this.parseStoredOverrides(LOCAL_STORAGE_KEY));
  }
  saveOverrides() {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this._overrides));
  }
  getOverrides() {
    return Object.fromEntries(Object.entries(this._overrides).map(([key, container]) => {
      const record = {};
      for (const [name, value] of Object.entries(container)) {
        if (Object.prototype.hasOwnProperty.call(container, _DJB2(name))) {
          record[name] = value;
        }
      }
      return [key, record];
    }));
  }
  setOverrides(overrides) {
    const newOverrides = {
      ...makeEmptyStore(),
      ...overrides
    };
    for (const container of Object.values(newOverrides)) {
      for (const [name, value] of Object.entries(container)) {
        const hash = _DJB2(name);
        if (!Object.prototype.hasOwnProperty.call(container, hash)) {
          container[hash] = value;
        }
      }
    }
    this._overrides = newOverrides;
    this.saveOverrides();
  }
  overrideGate(name, value) {
    this._overrides.gates[name] = value;
    this._overrides.gates[_DJB2(name)] = value;
    this.saveOverrides();
  }
  removeGateOverride(name) {
    delete this._overrides.gates[name];
    delete this._overrides.gates[_DJB2(name)];
    this.saveOverrides();
  }
  getGateOverride(current, _user) {
    var _this$_overrides$gate;
    const overridden = (_this$_overrides$gate = this._overrides.gates[current.name]) !== null && _this$_overrides$gate !== void 0 ? _this$_overrides$gate : this._overrides.gates[_DJB2(current.name)];
    if (overridden == null) {
      return null;
    }
    return {
      ...current,
      value: overridden,
      details: {
        ...current.details,
        reason: LOCAL_OVERRIDE_REASON
      }
    };
  }
  overrideDynamicConfig(name, value) {
    this._overrides.configs[name] = value;
    this._overrides.configs[_DJB2(name)] = value;
    this.saveOverrides();
  }
  removeDynamicConfigOverride(name) {
    delete this._overrides.configs[name];
    delete this._overrides.configs[_DJB2(name)];
    this.saveOverrides();
  }
  getDynamicConfigOverride(current, _user) {
    return this._getConfigOverride(current, this._overrides.configs);
  }
  overrideExperiment(name, value) {
    this._overrides.configs[name] = value;
    this._overrides.configs[_DJB2(name)] = value;
    this.saveOverrides();
  }
  removeExperimentOverride(name) {
    delete this._overrides.configs[name];
    delete this._overrides.configs[_DJB2(name)];
    this.saveOverrides();
  }
  getExperimentOverride(current, _user) {
    return this._getConfigOverride(current, this._overrides.configs);
  }
  overrideLayer(name, value) {
    this._overrides.layers[name] = value;
    this._overrides.layers[_DJB2(name)] = value;
    this.saveOverrides();
  }
  removeLayerOverride(name) {
    delete this._overrides.layers[name];
    delete this._overrides.layers[_DJB2(name)];
    this.saveOverrides();
  }
  removeAllOverrides() {
    this._overrides = makeEmptyStore();
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
  getLayerOverride(current, _user) {
    var _this$_overrides$laye;
    const overridden = (_this$_overrides$laye = this._overrides.layers[current.name]) !== null && _this$_overrides$laye !== void 0 ? _this$_overrides$laye : this._overrides.layers[_DJB2(current.name)];
    if (overridden == null) {
      return null;
    }
    return {
      ...current,
      __value: overridden,
      get: _makeTypedGet(current.name, overridden),
      details: {
        ...current.details,
        reason: LOCAL_OVERRIDE_REASON
      }
    };
  }
  _getConfigOverride(current, lookup) {
    var _lookup$current$name;
    const overridden = (_lookup$current$name = lookup[current.name]) !== null && _lookup$current$name !== void 0 ? _lookup$current$name : lookup[_DJB2(current.name)];
    if (overridden == null) {
      return null;
    }
    return {
      ...current,
      value: overridden,
      get: _makeTypedGet(current.name, overridden),
      details: {
        ...current.details,
        reason: LOCAL_OVERRIDE_REASON
      }
    };
  }
}